import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Loader2, Pencil, Link2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SetupAssistantDialog from "./SetupAssistantDialog";

const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (str) => str.split("\n").map((s) => s.trim()).filter(Boolean);

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-800 mt-1 whitespace-pre-line">{value || "—"}</p>
    </div>
  );
}

export default function BrandProfile() {
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () =>
    api.get("/marketing/brand-profile").then((r) => setProfile(r.data)).catch(() => setProfile(false));

  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setForm({
      ...profile,
      brand_colors: toLines(profile.brand_colors),
      content_pillars: toLines(profile.content_pillars),
      approved_terminology: toLines(profile.approved_terminology),
      prohibited_terminology: toLines(profile.prohibited_terminology),
    });
    setOpen(true);
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand_display_name: form.brand_display_name,
        brand_description: form.brand_description,
        primary_audience: form.primary_audience,
        secondary_audience: form.secondary_audience,
        brand_voice: form.brand_voice,
        value_proposition: form.value_proposition,
        brand_colors: fromLines(form.brand_colors),
        content_pillars: fromLines(form.content_pillars),
        approved_terminology: fromLines(form.approved_terminology),
        prohibited_terminology: fromLines(form.prohibited_terminology),
        clinical_compliance_rules: form.clinical_compliance_rules,
        default_cta: form.default_cta,
        default_cta_keyword: form.default_cta_keyword,
        primary_website: form.primary_website,
        default_landing_page: form.default_landing_page,
        review_mode: form.review_mode,
        active: form.active,
      };
      await api.put("/marketing/brand-profile", payload);
      toast.success("Brand profile updated");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (profile === null)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (profile === false)
    return <p className="text-sm text-red-600">Brand profile not found.</p>;

  return (
    <div data-testid="brand-profile">
      {!open ? (
        <>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold">{profile.brand_display_name}</h2>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 font-mono">
                <Link2 className="h-3.5 w-3.5" />
                Business Registry ID: <span data-testid="brand-business-id">{profile.business_id}</span>
              </div>
            </div>
            <Button onClick={startEdit} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="edit-brand-button">
              <Pencil className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => setSetupOpen(true)} variant="outline" className="border-[#581C87] text-[#581C87] hover:bg-[#F3E8FF]" data-testid="open-setup-assistant-button">
              <Wand2 className="h-4 w-4 mr-2" /> AI Setup Assistant
            </Button>
            <Button onClick={() => navigate("/marketing/media-settings")} variant="outline" className="border-gray-200" data-testid="open-media-settings-button">
              <Wand2 className="h-4 w-4 mr-2" /> Media Settings
            </Button>
          </div>
          <SetupAssistantDialog open={setupOpen} onOpenChange={setSetupOpen} onSaved={load} />

          <div className="flex items-center gap-3 mt-4">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${profile.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
              {profile.active ? "Active" : "Inactive"}
            </span>
            <span className="rounded-full px-3 py-1 text-xs font-semibold bg-[#F3E8FF] text-[#6B21A8]" data-testid="brand-review-mode">
              Review mode: {profile.review_mode}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 bg-white border border-gray-200 rounded-xl p-6">
            <Field label="Brand Description" value={profile.brand_description} />
            <Field label="Value Proposition" value={profile.value_proposition} />
            <Field label="Primary Audience" value={profile.primary_audience} />
            <Field label="Secondary Audience" value={profile.secondary_audience} />
            <Field label="Brand Voice" value={profile.brand_voice} />
            <Field label="Brand Colors" value={toLines(profile.brand_colors)} />
            <Field label="Content Pillars" value={toLines(profile.content_pillars)} />
            <Field label="Clinical & Compliance Rules" value={profile.clinical_compliance_rules} />
            <Field label="Approved Terminology" value={toLines(profile.approved_terminology)} />
            <Field label="Prohibited Terminology" value={toLines(profile.prohibited_terminology)} />
            <Field label="Default CTA" value={profile.default_cta} />
            <Field label="Default CTA Keyword" value={profile.default_cta_keyword} />
            <Field label="Primary Website" value={profile.primary_website} />
            <Field label="Default Landing Page" value={profile.default_landing_page} />
          </div>
        </>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4" data-testid="brand-edit-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Brand Display Name</Label>
              <Input value={form.brand_display_name || ""} onChange={(e) => upd("brand_display_name", e.target.value)} data-testid="brand-name-input" className="focus-visible:ring-[#581C87]" />
            </div>
            <div className="space-y-2">
              <Label>Business Registry ID</Label>
              <Input value={form.business_id || ""} disabled className="bg-gray-50 font-mono text-xs" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Brand Description</Label>
            <Textarea value={form.brand_description || ""} onChange={(e) => upd("brand_description", e.target.value)} className="focus-visible:ring-[#581C87]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Primary Audience</Label><Input value={form.primary_audience || ""} onChange={(e) => upd("primary_audience", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Secondary Audience</Label><Input value={form.secondary_audience || ""} onChange={(e) => upd("secondary_audience", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Brand Voice</Label><Input value={form.brand_voice || ""} onChange={(e) => upd("brand_voice", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Value Proposition</Label><Input value={form.value_proposition || ""} onChange={(e) => upd("value_proposition", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Brand Colors (one per line)</Label><Textarea value={form.brand_colors || ""} onChange={(e) => upd("brand_colors", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Content Pillars (one per line)</Label><Textarea value={form.content_pillars || ""} onChange={(e) => upd("content_pillars", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Approved Terminology (one per line)</Label><Textarea value={form.approved_terminology || ""} onChange={(e) => upd("approved_terminology", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Prohibited Terminology (one per line)</Label><Textarea value={form.prohibited_terminology || ""} onChange={(e) => upd("prohibited_terminology", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
          </div>
          <div className="space-y-2"><Label>Clinical & Compliance Rules</Label><Textarea value={form.clinical_compliance_rules || ""} onChange={(e) => upd("clinical_compliance_rules", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Default CTA</Label><Input value={form.default_cta || ""} onChange={(e) => upd("default_cta", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Default CTA Keyword</Label><Input value={form.default_cta_keyword || ""} onChange={(e) => upd("default_cta_keyword", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Primary Website</Label><Input value={form.primary_website || ""} onChange={(e) => upd("primary_website", e.target.value)} placeholder="https://..." className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Default Landing Page</Label><Input value={form.default_landing_page || ""} onChange={(e) => upd("default_landing_page", e.target.value)} placeholder="https://..." className="focus-visible:ring-[#581C87]" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Review Mode</Label>
              <Select value={form.review_mode} onValueChange={(v) => upd("review_mode", v)}>
                <SelectTrigger data-testid="brand-review-mode-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Review Required">Review Required</SelectItem>
                  <SelectItem value="Autopilot">Autopilot — Available in a later phase</SelectItem>
                </SelectContent>
              </Select>
              {form.review_mode === "Autopilot" && (
                <p className="text-xs text-amber-600">Autopilot is stored but will not publish anything. Available in a later phase.</p>
              )}
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
              <Label className="cursor-pointer">Active</Label>
              <Switch checked={!!form.active} onCheckedChange={(v) => upd("active", v)} data-testid="brand-active-switch" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="brand-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
